// Driving safety risk derived from visibility, temp/dew point spread, precip,
// and wind. Returns risks per category plus an overall level.

export interface DrivingInput {
  tempC: number;
  dewPointC?: number;
  visibilityMeters?: number;
  windMs: number;
  windGustMs?: number;
  precipMm?: number; // mm/h
  snowMm?: number; // mm/h
  conditionMain?: string; // OWM main e.g. Rain, Snow, Mist, Fog
  conditionId?: number;
}

export interface DrivingRisk {
  level: "Clear" | "Caution" | "Hazard" | "Severe";
  color: "emerald" | "amber" | "orange" | "red";
  score: number; // 0-100 risk (higher = worse)
  factors: { kind: string; severity: "low" | "med" | "high"; note: string }[];
  primary: string; // top reason
}

export function drivingRisk(input: DrivingInput): DrivingRisk {
  const factors: DrivingRisk["factors"] = [];
  let score = 0;

  // Fog / low visibility
  const vis = input.visibilityMeters ?? 10000;
  if (vis < 200) {
    score += 60;
    factors.push({ kind: "Fog", severity: "high", note: "Visibility under 200m" });
  } else if (vis < 1000) {
    score += 35;
    factors.push({ kind: "Fog", severity: "med", note: `Visibility ${(vis / 1000).toFixed(1)}km` });
  } else if (vis < 4000) {
    score += 15;
    factors.push({ kind: "Reduced visibility", severity: "low", note: `${(vis / 1000).toFixed(1)}km` });
  }

  // Mist/fog detection by condition (sometimes vis is 10000 but it's misty)
  const main = (input.conditionMain || "").toLowerCase();
  if (main === "fog" || main === "mist" || main === "haze") {
    if (vis >= 4000) {
      score += 10;
      factors.push({ kind: main === "fog" ? "Fog" : "Mist", severity: "low", note: "Reported in conditions" });
    }
  }

  // Ice / black ice risk: temp near or below freezing + recent precip + dew point close to temp
  const t = input.tempC;
  const dew = input.dewPointC;
  const wetSurface = (input.precipMm ?? 0) > 0.05 || (input.snowMm ?? 0) > 0.05;
  if (t <= 1 && t >= -6 && wetSurface) {
    score += 45;
    factors.push({ kind: "Ice", severity: "high", note: "Freezing + wet surface" });
  } else if (t <= 0 && dew != null && Math.abs(t - dew) <= 1) {
    score += 25;
    factors.push({ kind: "Frost / black ice", severity: "med", note: "Near freezing, saturated air" });
  } else if (t < 0) {
    score += 8;
    factors.push({ kind: "Cold surfaces", severity: "low", note: "Below freezing" });
  }

  // Snow
  const snow = input.snowMm ?? 0;
  if (snow > 5) {
    score += 50;
    factors.push({ kind: "Heavy snow", severity: "high", note: `${snow.toFixed(1)} mm/h` });
  } else if (snow > 1) {
    score += 25;
    factors.push({ kind: "Snow", severity: "med", note: `${snow.toFixed(1)} mm/h` });
  } else if (snow > 0.1 || main === "snow") {
    score += 12;
    factors.push({ kind: "Light snow", severity: "low", note: "Slippery patches possible" });
  }

  // Rain / hydroplaning
  const rain = input.precipMm ?? 0;
  if (rain > 7) {
    score += 35;
    factors.push({ kind: "Hydroplaning", severity: "high", note: `Heavy rain (${rain.toFixed(1)} mm/h)` });
  } else if (rain > 2) {
    score += 18;
    factors.push({ kind: "Wet roads", severity: "med", note: `Rain ${rain.toFixed(1)} mm/h` });
  } else if (rain > 0.2) {
    score += 6;
    factors.push({ kind: "Light rain", severity: "low", note: "Damp roads" });
  }

  // Wind / gusts (high-profile vehicle risk)
  const gust = input.windGustMs ?? input.windMs;
  if (gust >= 22) {
    score += 25;
    factors.push({ kind: "High winds", severity: "high", note: `Gusts ${gust.toFixed(0)} m/s` });
  } else if (gust >= 14) {
    score += 12;
    factors.push({ kind: "Strong winds", severity: "med", note: `Gusts ${gust.toFixed(0)} m/s` });
  } else if (gust >= 10) {
    score += 5;
    factors.push({ kind: "Breezy", severity: "low", note: `${gust.toFixed(0)} m/s` });
  }

  score = Math.min(100, score);

  const level: DrivingRisk["level"] =
    score >= 60 ? "Severe" : score >= 35 ? "Hazard" : score >= 15 ? "Caution" : "Clear";
  const color: DrivingRisk["color"] =
    score >= 60 ? "red" : score >= 35 ? "orange" : score >= 15 ? "amber" : "emerald";

  // Sort factors by severity for primary.
  const sevRank: Record<"low" | "med" | "high", number> = { high: 3, med: 2, low: 1 };
  const sorted = [...factors].sort((a, b) => sevRank[b.severity] - sevRank[a.severity]);
  const primary = sorted[0]?.kind ?? "No hazards";

  return { level, color, score, factors, primary };
}
